import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import {
  PRE_ACTIVATION_SCHEDULED_SESSION_DATE,
  PRE_ACTIVATION_SCHEDULED_SESSION_TIME,
  PRE_ACTIVATION_SCHEDULED_SESSION_TITLE,
} from "../../../i18n/keysTranslations";
import {
  INPUT_COLOR_BORDER,
  WHITE_DARK_COLOR,
} from "../../../utils/colors";
import { getLanguageFormatted } from "../../../utils/string";

const getValidDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateLabel = ({ startTime, language }) => {
  const startDate = getValidDate(startTime);
  if (!startDate) return typeof startTime === "string" ? startTime : "";

  return new Intl.DateTimeFormat(language, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(startDate);
};

const formatTimeLabel = ({ startTime, endTime, language }) => {
  const startDate = getValidDate(startTime);
  if (!startDate) return "";

  const timeOptions = { hour: "numeric", minute: "2-digit" };
  const startLabel = new Intl.DateTimeFormat(language, timeOptions).format(
    startDate
  );
  const endDate = getValidDate(endTime);
  if (!endDate) return startLabel;

  const endLabel = new Intl.DateTimeFormat(language, timeOptions).format(
    endDate
  );
  return `${startLabel} – ${endLabel}`;
};

const SummaryRow = ({ label, value }) =>
  value ? (
    <Box>
      <Typography
        sx={{
          fontSize: "11px",
          fontWeight: 700,
          letterSpacing: "0.4px",
          textTransform: "uppercase",
          color: "text.secondary",
          marginBottom: "2px",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "14px",
          color: "text.primary",
          lineHeight: 1.45,
        }}
      >
        {value}
      </Typography>
    </Box>
  ) : null;

const ScheduledSessionSummary = ({ session = {} }) => {
  const { t, i18n } = useTranslation();
  const language = getLanguageFormatted(i18n.language);
  const dateLabel = formatDateLabel({
    startTime: session.startTime,
    language,
  });
  const timeLabel = formatTimeLabel({
    startTime: session.startTime,
    endTime: session.endTime,
    language,
  });

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: "9px",
        backgroundColor: WHITE_DARK_COLOR,
        border: `1px solid ${INPUT_COLOR_BORDER}`,
      }}
    >
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 700,
          color: "text.primary",
          lineHeight: 1.4,
          marginBottom: dateLabel || timeLabel ? "10px" : 0,
        }}
      >
        {t(PRE_ACTIVATION_SCHEDULED_SESSION_TITLE)}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        <SummaryRow
          label={t(PRE_ACTIVATION_SCHEDULED_SESSION_DATE)}
          value={dateLabel}
        />
        <SummaryRow
          label={t(PRE_ACTIVATION_SCHEDULED_SESSION_TIME)}
          value={timeLabel}
        />
      </Box>
    </Box>
  );
};

export default ScheduledSessionSummary;
